import { ApolloError, useMutation } from '@apollo/client'
import { tap } from 'ramda'
import React, {
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { SanityParentProductFragment } from '../../../graphql/gatsby'
import {
  AddSimpleAndVirtualProductsToCartDocument,
  AddSimpleProductsToCartDocument,
  ConfigurableProductDetailsFragment,
  RemoveItemFromCartDocument,
  SimpleProductCartItemInput,
  SimpleProductDetailsFragment,
  VirtualProductCartItemInput,
} from '../../../graphql/magento'
import { ProductLabelStatus } from '../../../graphql/search'
import useLogAndCaptureError from '../../../hooks/useLogAndCaptureError'
import { TokenContext } from '../../../layouts/context'
import {
  publishAddToCartEventForItemInputs,
  publishAddToCartEventForItems,
} from '../../../lib/analytics/cart'
import { AnalyticsEventAddToCart } from '../../../lib/analytics/events/recommended'
import { ToggleCartContext, useCartContext } from '../../../lib/cart'
import { useAddToCartToast } from '../../../lib/cart/hooks/useAddToCartToast'
import {
  correspondingLabelDesignItem,
  isVirtualCartItem,
  labelDesignFeeCartItemInput,
  labelDesignItemValues,
} from '../../../lib/cart/item'
import {
  formatQuantityError,
  isActiveCartError,
  isMaxOrMinQtyError,
  isOutOfStockError,
  isQuantityError,
} from '../../../lib/cart/item-control-options'
import {
  requestCheckCartItemsNeedingUpdate,
  resetCartItemsUpdating,
  setCartItemsUpdating,
  useAutoUpdateErrantCartItems,
} from '../../../lib/cart/state/cart-items/cart-item-update'
import { closeQuickViewProductModal } from '../../../lib/products/quick-view-modal'
import { logAndCaptureException } from '../../../utils/errorTools'
import { checkQueryResponse } from '../../../utils/graphqlTools'
import { availableVariantSelections, formattedVariantData } from '../../../utils/itemTools'
import ContactSupportMessage from '../../ContactSupportMessage'
import ItemControls, { AddToCart, AddToCartOptions, ItemControlsProps } from './ItemControls'

export interface ItemControlsContainerProps {
  item: ConfigurableProductDetailsFragment
  onVariantSelected: (itemValue: string) => void
  sanityParentProduct?: SanityParentProductFragment | null
  selectedVariant: SimpleProductDetailsFragment | null | undefined
  variantLabelStatus: ProductLabelStatus | undefined
  analyticsEventAppContext?: AnalyticsEventAddToCart['app_context']
  containerType: ItemControlsProps['containerType']
  className?: string
}

const ItemControlsContainer: FC<ItemControlsContainerProps> = ({
  item,
  onVariantSelected,
  selectedVariant = null,
  variantLabelStatus,
  analyticsEventAppContext,
  sanityParentProduct,
  containerType,
  className,
}) => {
  const { cart, refetch } = useCartContext()
  const token = useContext(TokenContext)
  const { isCartOpen, toggleCart } = useContext(ToggleCartContext)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<ReactElement | null>(null)
  const [retryAddToCart, setRetryAddToCart] = useState<{
    addToCartOptions: AddToCartOptions
    cartId: string
  } | null>()
  const { showSuccessToast, showRemovedToast } = useAddToCartToast()
  const { loading: autoUpdateItemsLoading } = useAutoUpdateErrantCartItems()

  const [
    addSimpleProductsToCart,
    { error: addSimpleProductsMutationError, loading: addSimpleProductsLoading },
  ] = useMutation(AddSimpleProductsToCartDocument, {
    context: { token },
  })
  const [
    addSimpleAndVirtualProductsToCart,
    { error: addSAVProductsMutationError, loading: addSAVProductsLoading },
  ] = useMutation(AddSimpleAndVirtualProductsToCartDocument, {
    context: { token },
  })
  const [removeItemFromCart, { error: removalError }] = useMutation(RemoveItemFromCartDocument, {
    context: { token },
  })

  const foundActiveCartError = useRef(false)
  const foundOutOfStockError = useRef(false)
  const foundMaxOrMinQuantityError = useRef(false)

  useLogAndCaptureError(removalError)
  const mutationError = addSimpleProductsMutationError || addSAVProductsMutationError
  useLogAndCaptureError(mutationError)

  const isActiveCartErrorPresent = (err: ApolloError): boolean =>
    !!err.graphQLErrors.find((graphQlError) => isActiveCartError(graphQlError.message))
  const isOutOfStockErrorPresent = (err: ApolloError): boolean =>
    !!err.graphQLErrors.find((graphQlError) => isOutOfStockError(graphQlError.message))
  const isMaxOrMinQtyErrorPresent = (err: ApolloError): boolean =>
    !!err.graphQLErrors.find((graphQlError) => isMaxOrMinQtyError(graphQlError.message))
  const getQuantityErrorMessage = (err: ApolloError): string | undefined =>
    err.graphQLErrors.find((graphQLError) => isQuantityError(graphQLError.message))?.message
  const hasOutOfStockItems = useCallback(
    () => !!cart?.items?.find((cartItem) => cartItem?.product?.stock_status === 'OUT_OF_STOCK'),
    [cart],
  )

  const addToCart = useMemo<AddToCart | null>(() => {
    if (!cart?.id || !selectedVariant?.sku || autoUpdateItemsLoading) {
      return null
    }
    return (input) => {
      const {
        qty,
        orderType,
        autoShipInterval,
        desiredName,
        additionalDetails,
        payUpfrontOptionId,
        payUpfrontOptionValue,
      } = input
      setRetryAddToCart(null)
      setError(null)
      setSubmitting(true)
      foundActiveCartError.current = false
      foundOutOfStockError.current = false
      foundMaxOrMinQuantityError.current = false

      const sku = `${selectedVariant.sku}`
      const quantity = parseInt(qty, 10)

      const simpleCartItems: SimpleProductCartItemInput[] = [
        {
          data: {
            sku,
            quantity,
            dfh_order_type: parseInt(`${orderType}`, 10),
            dfh_autoship_interval: parseInt(autoShipInterval, 10),
          },
          ...(payUpfrontOptionId &&
            payUpfrontOptionValue && {
              customizable_options: [
                {
                  id: parseInt(payUpfrontOptionId, 10),
                  value_string: payUpfrontOptionValue,
                },
              ],
            }),
        },
      ]

      // build virtual cart item input for label design fee if `desiredName` is present
      const virtualCartItems: VirtualProductCartItemInput[] | undefined = desiredName
        ? [
            labelDesignFeeCartItemInput({
              desiredName,
              additionalDetails,
              forSku: sku,
            }),
          ]
        : undefined

      // vary promise chain by whether we're adding a virtual product in conjunction with the simple product
      const mutationPromise = virtualCartItems
        ? addSimpleAndVirtualProductsToCart({
            variables: {
              cartId: cart.id,
              simpleCartItems,
              virtualCartItems,
            },
          })
            .then(checkQueryResponse)
            .then(
              tap(
                publishAddToCartEventForItemInputs({
                  simpleCartItems,
                  virtualCartItems,
                  appContext: analyticsEventAppContext,
                }),
              ),
            )
            .then((data) => {
              if (
                data?.addSimpleProductsToCart &&
                data?.addVirtualProductsToCart &&
                selectedVariant?.sku
              ) {
                closeQuickViewProductModal()
                showSuccessToast({ skus: [selectedVariant.sku] })
              }
            })
        : addSimpleProductsToCart({
            variables: {
              cartId: cart.id,
              cartItems: simpleCartItems,
            },
          })
            .then(checkQueryResponse)
            .then(
              tap(
                publishAddToCartEventForItems({
                  items: [simpleCartItems[0].data],
                  appContext: analyticsEventAppContext,
                }),
              ),
            )
            .then((data) => {
              if (data?.addSimpleProductsToCart && selectedVariant?.sku) {
                closeQuickViewProductModal()
                showSuccessToast({ skus: [selectedVariant.sku] })
              }
            })

      mutationPromise
        .catch((err: unknown) => {
          if (!(err instanceof ApolloError)) {
            logAndCaptureException(err)
            return
          }
          if (isActiveCartErrorPresent(err)) {
            foundActiveCartError.current = true
            refetch && refetch()
            setRetryAddToCart({ addToCartOptions: input, cartId: cart.id })
            return
          }
          if (isOutOfStockErrorPresent(err)) {
            foundOutOfStockError.current = true
            !hasOutOfStockItems() && refetch && refetch()
            setRetryAddToCart({ addToCartOptions: input, cartId: cart.id })
            return
          }
          if (isMaxOrMinQtyErrorPresent(err)) {
            foundMaxOrMinQuantityError.current = true
            requestCheckCartItemsNeedingUpdate()
            setRetryAddToCart({ addToCartOptions: input, cartId: cart.id })
            if (!isCartOpen) {
              toggleCart()
            }
          }
        })
        .finally(() => {
          !foundOutOfStockError.current && !foundActiveCartError.current && setSubmitting(false)
        })
    }
  }, [
    cart,
    selectedVariant,
    addSimpleAndVirtualProductsToCart,
    addSimpleProductsToCart,
    showSuccessToast,
    refetch,
    toggleCart,
    isCartOpen,
    hasOutOfStockItems,
    analyticsEventAppContext,
    autoUpdateItemsLoading,
  ])

  const removeOutOfStockItemsAndRetryAddToCart = useCallback(
    (addToCartOptions: AddToCartOptions) =>
      cart?.items?.forEach(
        (cartItem) =>
          cartItem?.product?.stock_status === 'OUT_OF_STOCK' &&
          removeItemFromCart({
            variables: {
              cartId: cart.id,
              itemId: Number(cartItem?.id),
            },
          })
            .then(checkQueryResponse)
            .then((data) => {
              if (data?.removeItemFromCart && cartItem?.product?.sku) {
                showRemovedToast({ skus: [cartItem?.product?.sku] })
                addToCart && addToCart(addToCartOptions)
              }
            })
            .catch((err: unknown) => {
              !(err instanceof ApolloError) && logAndCaptureException(err)
            }),
      ),
    [addToCart, cart, removeItemFromCart, showRemovedToast],
  )

  // Set sku for cartItemsUpdating$ to indicate item being updated in cart component
  useEffect(() => {
    if ((addSimpleProductsLoading || addSAVProductsLoading) && selectedVariant?.sku) {
      setCartItemsUpdating([selectedVariant.sku])
    } else {
      resetCartItemsUpdating()
    }
  }, [addSAVProductsLoading, addSimpleProductsLoading, selectedVariant?.sku])

  useEffect(() => {
    if (mutationError) {
      if (isActiveCartErrorPresent(mutationError) || isOutOfStockErrorPresent(mutationError)) {
        return
      }

      const quantityErrorMessage = getQuantityErrorMessage(mutationError)
      if (quantityErrorMessage) {
        const isForCurrentItem = selectedVariant?.sku
          ? new RegExp(`${selectedVariant?.sku}`).test(quantityErrorMessage)
          : false
        const formattedQuantityErrorMessage = formatQuantityError(
          quantityErrorMessage,
          isForCurrentItem
            ? 'Unable to add product to cart'
            : 'Unable to add product to cart because another item in your cart had error',
        )
        setError(<>{formattedQuantityErrorMessage}</>)
        return
      }

      setError(
        <>
          Unable to add product to cart. <ContactSupportMessage />
        </>,
      )
    }
  }, [mutationError, retryAddToCart, addToCart, selectedVariant?.sku])

  // Retry addToCart once refetch updates the cart id
  useEffect(() => {
    if (!retryAddToCart || !cart || !addToCart) {
      return
    }
    const { addToCartOptions, cartId } = retryAddToCart
    if (foundActiveCartError && cartId !== cart.id) {
      addToCart(addToCartOptions)
    }
    if (foundOutOfStockError) {
      removeOutOfStockItemsAndRetryAddToCart(addToCartOptions)
    }
    if (foundMaxOrMinQuantityError && !autoUpdateItemsLoading) {
      addToCart(addToCartOptions)
    }
  }, [
    addToCart,
    autoUpdateItemsLoading,
    cart,
    removeOutOfStockItemsAndRetryAddToCart,
    retryAddToCart,
    toggleCart,
  ])

  const variantData = useMemo(
    () => formattedVariantData({ variant_index: item.variant_index || {} }),
    [item],
  )
  const availableVariantOptions = useMemo(
    () => availableVariantSelections({ ...variantData }),
    [variantData],
  )
  const quantityInCart = useMemo(() => {
    if (cart?.items && selectedVariant) {
      const matchingCartItem = cart.items.find(
        (cartItem) => cartItem?.product.sku === selectedVariant.sku,
      )
      return matchingCartItem ? matchingCartItem.quantity : 0
    }
    return undefined
  }, [selectedVariant, cart?.items])
  const labelInformationInCart = useMemo(() => {
    if (cart?.items && selectedVariant) {
      const matchingCartItem = cart.items.find(
        (cartItem) => cartItem?.product.sku === selectedVariant.sku,
      )
      const labelDesignItem = matchingCartItem
        ? correspondingLabelDesignItem(matchingCartItem, cart.items)
        : undefined
      if (labelDesignItem) {
        return isVirtualCartItem(labelDesignItem)
          ? labelDesignItemValues(labelDesignItem)
          : undefined
      }
    }
    return undefined
  }, [selectedVariant, cart?.items])

  return (
    <ItemControls
      className={className}
      containerType={containerType}
      item={item}
      sanityParentProduct={sanityParentProduct}
      variantData={variantData}
      availableVariantOptions={availableVariantOptions}
      addToCart={addToCart}
      error={error}
      submitting={submitting}
      onVariantSelected={onVariantSelected}
      selectedVariant={selectedVariant}
      variantLabelStatus={variantLabelStatus}
      quantityInCart={quantityInCart}
      itemUrlKey={item?.url_key}
      labelInformationInCart={labelInformationInCart}
    />
  )
}

export default ItemControlsContainer
